import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, CardTitle, Button, Form, FormGroup, Input } from "reactstrap";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import ToastNotification from "components/Toast/ToastNotification";
import slash from "assets/icons/slash-circle.svg";
import { useSendConfirmOtpMutation, useVerifyConfirmOtpMutation } from "../../../services/auth/api";
import { useFormik } from "formik";
import * as Yup from "yup";

const OTP_RESEND_COOLDOWN = 300; // 5 minutes in seconds

// Validation schema for OTP form
const otpValidationSchema = Yup.object().shape({
  otp: Yup.string()
    .required("กรุณากรอกรหัส OTP")
    .matches(/^\d{6}$/, "รหัส OTP ต้องเป็นตัวเลข 6 หลัก"),
});

const LoginOtp = ({ userData, onSuccess }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [otpToken, setOtpToken] = useState("");
  const [refNo, setRefNo] = useState("");
  const [canResend, setCanResend] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [sendOtp, { isLoading: isSendingOtp }] = useSendConfirmOtpMutation();
  const [verifyOtp] = useVerifyConfirmOtpMutation();

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: otpValidationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        const res = await verifyOtp({ email: userData.email, token: otpToken, otp_code: values.otp }).unwrap();
        if (res?.success) {
          onSuccess();
        } else {
          throw new Error("VERIFY_FAILED");
        }
      } catch (error) {
        setFieldError("otp", "รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง");
        let id = toast.custom(<ToastNotification variant="danger" icon={slash} title="ยืนยันตัวไม่สำเร็จ" description="กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => toast.dismiss(id)} />, { position: "top-right", duration: 5000 });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Format time in MM:SS format
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle OTP input change - format to only allow numbers and limit to 6 digits
  const handleOtpChange = (e, handleChange) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    handleChange("otp")(value);
  };

  // Send OTP
  const handleSendOtp = useCallback(async () => {
    if (!canResend) return;

    try {
      const res = await sendOtp({ email: userData.email }).unwrap();
      const token = res?.data?.token ?? res?.token;
      const ref = res?.data?.ref_no ?? res?.ref_no;
      if (token) setOtpToken(token);
      if (ref) setRefNo(ref);
      setTimeLeft(OTP_RESEND_COOLDOWN);
      setCanResend(false);
      setIsInitialLoad(false);
      formik.resetForm();
    } catch (error) {
      let id = toast.custom(<ToastNotification variant="danger" icon={slash} title="ส่งรหัส OTP ไม่สำเร็จ" description="ไม่สามารถส่งรหัส OTP ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => toast.dismiss(id)} />, { position: "top-right", duration: 5000 });
    }
  }, [canResend, sendOtp, userData.email, formik]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Send OTP on initial load
  useEffect(() => {
    if (isInitialLoad && userData?.email) {
      handleSendOtp();
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, userData?.email, handleSendOtp]);

  return (
    <>
      <Card className="card-auth">
        <CardTitle className="text-center">
          <h1 className="text-4xl fw-semibold">ยืนยันตัวตนของคุณ</h1>
        </CardTitle>
        <CardBody>
          <Form onSubmit={formik.handleSubmit} className="w-full">
            <h4 className="text-center text-gray-500 mb-3">
              เราได้ส่งรหัส OTP ไปยังอีเมลของท่านแล้ว <br />
              กรุณาตรวจสอบรหัสที่ได้รับกรอกด้านล่างนี้
            </h4>

            {refNo && (
              <h4 className="text-center text-gray-500 mb-8">
                รหัสอ้างอิง: <strong>{refNo}</strong>
              </h4>
            )}

            <FormGroup>
              <Input type="text" name="otp" className={`form-control text-center ${formik.touched.otp && formik.errors.otp ? "is-invalid" : ""}`} placeholder="_ _ _ _ _ _" maxLength={6} value={formik.values.otp} onChange={(e) => handleOtpChange(e, formik.handleChange)} onBlur={formik.handleBlur} disabled={formik.isSubmitting} style={{ paddingRight: "1rem" }} />
              {formik.touched.otp && formik.errors.otp && <div className="invalid-feedback d-block text-center">{formik.errors.otp}</div>}
            </FormGroup>

            <div className="mb-4">
              <Button type="submit" color="primary" className="w-100 mb-4" size="lg" disabled={!formik.isValid || formik.isSubmitting}>
                {formik.isSubmitting ? "กำลังยืนยัน..." : "ยืนยัน"}
              </Button>
            </div>

            <div className="text-center">
              {!canResend && timeLeft > 0 ? (
                <h5 className="text-gray-500">
                  ส่งรหัสอีกครั้งได้ใน <strong>{formatTime(timeLeft)}</strong>
                </h5>
              ) : (
                <h5 className="fw-bold">
                  ยังไม่ได้รับรหัสยืนยันใช่ไหม?{" "}
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    className="text-primary hover:text-primary-hover sm:text-base fw-bold text-decoration-underline text-decoration-underline-offset-4 transition"
                    disabled={isSendingOtp}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: isSendingOtp ? "not-allowed" : "pointer",
                    }}
                  >
                    {isSendingOtp ? "กำลังส่งใหม่..." : "ส่งอีกครั้ง"}
                  </button>
                </h5>
              )}
            </div>
          </Form>
        </CardBody>
      </Card>
    </>
  );
};

LoginOtp.propTypes = {
  userData: PropTypes.shape({
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default LoginOtp;
